import { FC, ReactElement, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Stack, Tab, Tabs } from "@mui/material";

import { POTENTIAL_TABS, POTENTIAL_TAB_KEYS } from "@/constants";
import { tokens } from "@/tokens";
import { useFilteredTabs } from "@/hooks/useFilteredTabs";

const PotentialSavingTabs: FC = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const filteredTabs = useFilteredTabs(POTENTIAL_TABS, POTENTIAL_TAB_KEYS, "POTENTIAL_SAVINGS");

  const activeTab = useMemo(() => {
    const currentPath = location.pathname.split("/").pop() || "";
    const idx = filteredTabs.findIndex((tab) => tab.link === currentPath);
    return idx === -1 ? 0 : idx;
  }, [location.pathname, filteredTabs]);

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop() || "";
    const found = filteredTabs.some((tab) => tab.link === currentPath);
    if (!found && filteredTabs.length > 0) {
      navigate(`/dashboard/potential/${filteredTabs[0].link}`, { replace: true });
    }
  }, [location.pathname, filteredTabs, navigate]);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = filteredTabs[newValue];
    if (!tab) return;
    navigate(`/dashboard/potential/${tab.link}`);
  };

  return (
    <Stack
      spacing={0}
      sx={{
        margin: {
          xs: "-6px -10px 0 -10px",
          md: "-12px -20px 0 -20px",
        },
        width: {
          xs: "calc(100% + 20px)",
          md: "calc(100% + 40px)",
        },
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${tokens.border.default}`,
          width: "100%",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ minHeight: 36, px: { xs: 2, md: 3 } }}
        >
          <Tabs
            value={activeTab}
            onChange={handleChange}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: tokens.fontWeight.bold,
                fontSize: tokens.fontSize.xl,
                minHeight: 36,
                padding: "6px 12px",
                mr: 0.5,
                color: tokens.text.secondary,
                "&:hover": {
                  color: tokens.brand.primary,
                  backgroundColor: "transparent",
                },
                "&.Mui-selected": {
                  color: tokens.brand.primary,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: tokens.brand.primary,
                height: 2,
                bottom: 0,
              },
            }}
          >
            {filteredTabs.map((tab) => (
              <Tab
                key={tab.link}
                label={tab.text}
                sx={{ gap: 0.5, py: 0 }}
              />
            ))}
          </Tabs>
          {/* Spacer keeps future right-aligned actions balanced with tabs */}
          <Box sx={{ width: 32 }} />
        </Stack>
      </Box>
    </Stack>
  );
};

export default PotentialSavingTabs;

// Re-export the components for routing
export { default as PreferentialOrigin } from "./preferential-origin";
export { default as FTZ } from "./ftz";
export { default as Exclusions } from "./exclusions";
